import classnames from 'classnames'
import { useHash } from '@/hooks/useHash'
import { Modal, Text, usePopoverContext } from '@lounge-fe/ui-kit'
import { useEffect } from 'react'
import { Video } from './Video'
import { ModalGroupModalProps } from './types'
import { BlockRenderer, type BlockSettings } from '../BlockRenderer'
import { useShopify } from '@/hooks/useShopify'

export const BaseModalGroupModal = ({
  blocks,
  size,
  isMobileFullscreen,
  title,
  subtitle,
  sectionId,
  modalId,
  aspectRatio,
  video,
  enableFullscreen,
}: ModalGroupModalProps) => {
  const { isOpen, onOpen, onClose } = usePopoverContext()
  const [hash, updateHash] = useHash()
  const { designMode: isDesignMode } = useShopify()

  /**
   * In Shopify design mode, open the modal when this section is selected,
   * and close it when the section is deselected.
   */
  useEffect(() => {
    if (!isDesignMode) return

    const handleSectionSelect = (event: Event) => {
      const customEvent = event as CustomEvent<{ sectionId?: string }>
      if (customEvent.detail?.sectionId === sectionId) {
        onOpen()
      }
    }

    const handleSectionDeselect = (event: Event) => {
      const customEvent = event as CustomEvent<{ sectionId?: string }>
      if (customEvent.detail?.sectionId === sectionId) {
        onClose()
      }
    }

    document.addEventListener('shopify:section:select', handleSectionSelect)
    document.addEventListener('shopify:section:deselect', handleSectionDeselect)

    return () => {
      document.removeEventListener(
        'shopify:section:select',
        handleSectionSelect,
      )
      document.removeEventListener(
        'shopify:section:deselect',
        handleSectionDeselect,
      )
    }
  }, [isDesignMode, sectionId, onOpen, onClose])

  useEffect(() => {
    if (hash && hash.replace('#', '') === modalId) {
      onOpen()
    }
  }, [hash])

  useEffect(() => {
    if (!isOpen && hash === `#${modalId}`) {
      updateHash('')
    }
  }, [isOpen])

  const hasVideo = video

  const hasHeader = title || subtitle

  const headerClass = !hasHeader ? 'md:p-0 !border-none' : undefined

  return (
    <>
      {hasVideo ? (
        <Modal.Content
          size={enableFullscreen ? undefined : '3xl'}
          isMobileFullscreen={true}
          className={classnames(
            'rounded-sm overflow-hidden',
            enableFullscreen &&
              'fixed inset-0 !max-w-none !max-h-none w-screen h-screen !m-0 rounded-none',
          )}
        >
          <Video video={video} isFullscreen={enableFullscreen} />

          <Modal.CloseButton className="!right-6 !bg-transparent [&_rect]:fill-white" />
        </Modal.Content>
      ) : (
        <Modal.Content
          id={`shopify-section-${sectionId}`}
          size={size}
          isMobileFullscreen={isMobileFullscreen}
        >
          <Modal.Header className={headerClass}>
            {title && (
              <Text
                dangerouslySetInnerHTML={{ __html: title }}
                variant="title-four"
                className="modal__title"
              />
            )}
            {subtitle && (
              <Text
                dangerouslySetInnerHTML={{ __html: subtitle }}
                variant="body-md"
                className="modal__subtitle text-subtle mt-3"
              />
            )}
            <Modal.CloseButton />
          </Modal.Header>

          <Modal.Body
            aspectRatio={aspectRatio}
            className={classnames(
              'flex flex-1 flex-col gap-4',
              !hasHeader && '!p-8',
            )}
          >
            <BlockRenderer<BlockSettings> blocks={blocks} />
          </Modal.Body>
        </Modal.Content>
      )}
    </>
  )
}

export const ModalGroupModal = (props: ModalGroupModalProps) => {
  return (
    <Modal>
      <BaseModalGroupModal {...props} />
    </Modal>
  )
}
